import React, { useState, useEffect, useRef } from 'react';

const VideoPlayer = ({ src, autoPlay = true, muted = true, loop = true, style = {} }) => {
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = useRef(null);
  const maxRetries = 3;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleError = (e) => {
      console.error('Video Error Details:', {
        event: e,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.currentSrc,
        error: video.error,
        retryCount
      });

      if (retryCount < maxRetries) {
        console.log(`Retrying video load (${retryCount + 1}/${maxRetries})...`);
        setRetryCount(prev => prev + 1);
        // Reset the video source to trigger a reload
        const currentSrc = video.src;
        video.src = '';
        setTimeout(() => {
          video.src = currentSrc;
          video.load();
        }, 1000 * (retryCount + 1)); // Exponential backoff
      } else {
        setError(e);
      }
    };

    const handleLoadStart = () => {
      console.log('Video load started:', src);
    };

    const handleCanPlay = () => {
      console.log('Video can play:', src);
      setError(null); // Clear any previous errors
    };

    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded:', src);
    };

    const handleLoadedData = () => {
      console.log('Video data loaded:', src);
    };

    const handleProgress = (e) => {
      if (video.buffered.length > 0) {
        console.log('Video buffering progress:', 
          (video.buffered.end(video.buffered.length - 1) / video.duration * 100).toFixed(2) + '%'
        );
      }
    };

    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('progress', handleProgress);
    };
  }, [src, retryCount]);

  if (error) {
    console.error('Video loading error:', error);
    return (
      <div style={{ color: 'red', padding: '1rem' }}>
        Error loading video. Please try refreshing the page.
        <br />
        <small>Error details: {error.message}</small>
      </div>
    );
  }

  // Mobile detection for optimizations
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline
      style={{ 
        width: '100%', 
        height: 'auto',
        display: 'block',
        position: 'relative',
        zIndex: 0,
        objectFit: 'cover',
        ...style
      }}
      preload={isMobile ? "metadata" : "auto"}
      crossOrigin="anonymous"
    />
  );
};

export default VideoPlayer; 